import * as React from 'react';
import classnames from 'classnames';
import isPast from 'date-fns/isPast';
import Sticky from 'react-stickynode';
import {useLocation} from 'react-router';
import isFuture from 'date-fns/isFuture';
import {FormattedMessage} from 'react-intl';
import {useEffect, useState, useMemo} from 'react';
import { InView } from 'react-intersection-observer';

import {
    useUiStore,
    selectInterestModalVisibility,
    selectRedirectMessageVisibility,
} from 'features/ui/store/useUiStore';
import {ENV, SITE, LinkStyle} from 'features/ui/enums';
import {SearchResult} from "features/search/types";
import {SITE_URLS} from 'features/search/constants';
import {ModalPosition, useModal} from "features/modal/hooks/useModal";
import {CookieStore} from 'features/cookies/cookie-store';
import {useAuth} from "features/authentication/hooks/useAuth";
import {GalleryType, PageModel} from "features/template/types";
import {hasShowing} from 'features/services/utils/showing-helpers';
import {useBreadcrumbs} from "features/template/ViewModelProvider";
import {cleanFinnishNames} from 'features/services/utils/name-helpers';
import {getFormattedMessage} from 'features/services/utils/language-helper';
import {getCurrentEnv, getCurrentSite} from 'features/services/utils/env-helper';
import { useDeviceStore, selectIsMobile } from 'features/ui/store/useDeviceStore';
import {ResidenceStageViewModel} from "features/template/viewModels/ResidenceStage";
import {sanitizeHtml} from 'features/services/utils/sanitize-html';
import {SearchService, useParkingSpaces} from 'features/search/services/SearchService';
import {gtmEventsStagePageStickyClick} from 'features/services/googleTagManager/events';
import {getVIPPurchaseEnd, getVIPPurchaseStart} from 'features/services/utils/date-helpers';
import {ConceptType, DisposalForm, ResidenceType, StageStatus} from 'features/search/filters/enums';
import {useMetaStore, selectCookieSettings, selectIsInEditMode} from "features/meta/store/useMetaStore";
import {convertFiltersForApi, getSiteIdForQuery, initialFilterState} from 'features/search/helpers';

import './style.scss';

import {A} from 'components/atoms/LinksAndButtons/A';
import {P} from 'components/atoms/Text/P';
import {MainGrid} from 'components/atoms/Layout/MainGrid';
import {Container} from 'components/atoms/Layout/Container';
import {UserAgent} from 'components/atoms/UserAgent';
import {InterestForm} from "components/forms/InterestForm";
import {AccessibleSlider} from '@molecules/SlidingContent/AccessibleSlider';
import {StructuredData} from '@molecules/StructuredData';
import {InfoMessage} from 'components/molecules/Banners/InfoMessage';
import {ContentArea} from 'components/organisms/GeneralComponents/ContentArea';
import {CampaignBlock} from 'components/blocks/Text/CampaignBlock';
import {MapWrapper} from 'components/organisms/Map/MapWrapper';

import {ImageHeader} from "@organisms/ContentBlocks/ImageHeader";
import {ImageGallery} from "@organisms/ContentBlocks/ImageGallery";
import {CoOwningBanner} from 'components/molecules/Banners/CoOwningBanner';
import {VipPeriodBanner} from 'components/molecules/Banners/VipPeriodBanner';
import {RedirectMessage} from 'components/molecules/ResidenceSearchComponents/RedirectMessage';
import {ProjectTimeline} from 'components/molecules/SlidingContent/ProjectTimeline';
import {SiteSpecificContent} from 'components/atoms/SiteSpecificContent';
import {InterestFormModal} from 'components/modals/General/InterestFormModal';
import {ImageHeaderMenu} from 'components/organisms/ContentBlocks/ImageHeader/ImageHeaderMenu';
import {InterestButton} from '@organisms/ContentBlocks/ImageHeader/ResidenceActionButton/_interestButton';

import {Concept} from 'components/organisms/ResidenceSections/_concept';
import {LabelSection} from 'components/organisms/ResidenceSections/_labels';
import {ResidenceAside} from 'components/organisms/ResidenceSections/_residenceAside';
import {MainInfo, QuickFactsType} from 'components/organisms/ResidenceSections/_mainInfo';

import { ParkingLink } from '@molecules/Banners/ParkingLink';
import {ResidenceExplorerPageType} from "features/residenceExplorer/types";
import {PriceListV2} from "features/residenceExplorer/components/PriceListV2";
import { Parkings } from 'features/residenceExplorer/components/PriceListV2/_parkings';
import {ResidenceExplorerV2} from "features/residenceExplorer/components/ResidenceExplorerV2";
import {ExternalExplorerBlock} from "features/residenceExplorer/components/ExternalExplorerBlock";
import {useResidenceExplorerStore, selectExplorerObjects} from "features/residenceExplorer/store/useResidenceExplorerStore";
import { ResidenceCardType, ResidenceCard } from '@molecules/ResidentialCards/ResidenceCard';

type Props = PageModel<ResidenceStageViewModel>;

export const StagePage: React.FunctionComponent<Props> = ({ viewModel }) => {
    const env = getCurrentEnv();
    const { open } = useModal();
    const interestModalVisibility = useUiStore(selectInterestModalVisibility);
    const showRedirectMessage = useUiStore(selectRedirectMessageVisibility);
    const setInterestModalVisibility = useUiStore((state) => state.setInterestModalVisibility);

    const {user, isLoggedIn} = useAuth();
    const objects = useResidenceExplorerStore(selectExplorerObjects);
    const {data: parkingPlaces} = useParkingSpaces([viewModel.stageId]); 
    const isMobile = useDeviceStore(selectIsMobile);
    const userCookieSettings = useMetaStore(selectCookieSettings);
    const breadcrumbs = useBreadcrumbs();
    const isInEditMode = useMetaStore(selectIsInEditMode);

    const vipPeriod = viewModel.stageStatus === StageStatus.VIP && isFuture(new Date(viewModel.vipEnd));

    // Etappen säljer bostäder — på VIP-etapper bara till VIP-kunder.
    const residencesAvailable =
        (viewModel.stageStatus === StageStatus.VIP && user?.isVip) ||
        viewModel.stageStatus === StageStatus.FORSALE ||
        viewModel.stageStatus === StageStatus.MOVE_IN_READY ||
        viewModel.stageStatus === StageStatus.PRESELLING;

    // Styr både bostadssektionen och menyvalet som scrollar dit. Menyvalet får inte
    // härledas ur explorer-storen ensam — den kan ligga kvar fylld från en tidigare
    // VIP-session och gav då en flik som scrollade till en sektion som inte fanns.
    const residencesVisible = residencesAvailable || (isInEditMode && viewModel.hasExplorer);

    const [_areaName, _setAreaName] = useState<string>('');
    const location = useLocation();

    const [bellowFold, setBellowFold] = useState<boolean>(false);
    //stages
    const [relatedStages, setRelatedStages] = useState<SearchResult[]>([]);
    const [relatedStagesSearchLink, setRelatedStagesSearchLink] = useState<string>('');
    const [connectedStages, setConnectedStages] = useState<SearchResult[]>([]);
    //user
    const [showInterestButton, setShowInterestButton] = useState<boolean>(true);
    const [interestDone, setInterestDone] = useState<boolean>(false);
    //map
    const [mapMarkers, setMapMarkers] = useState<{ residence: SearchResult, position: { lat: number | undefined; lng: number | undefined } }[]>([]);

    const scrollRefs = useMemo(() => ({
        overview: React.createRef<HTMLDivElement>(),
        threeSixty: React.createRef<HTMLDivElement>(),
        facts: React.createRef<HTMLDivElement>(),
        showings: React.createRef<HTMLDivElement>(),
        images: React.createRef<HTMLDivElement>(),
        interest: React.createRef<HTMLDivElement>(),
        objects: React.createRef<HTMLDivElement>(),
        about: React.createRef<HTMLDivElement>(),
        map: React.createRef<HTMLDivElement>(),
    }), []);

    React.useEffect(() => {
        setupContent();

        return () => {
            setInterestModalVisibility(false);
        }
    }, []);

    useEffect(() => {
        if (!user) return;

        if (interestModalVisibility) {
            open(
                <InterestFormModal
                    stageName={viewModel.headerDisplay}
                    agent={viewModel.realEstateAgents && viewModel.realEstateAgents[0]}
                    stageId={viewModel.stageId}
                    stageStatus={viewModel.stageStatus}
                    interestType="default"
                    interestDone={interestDone}
                    setInterestDone={setInterestDone}
                    hideMessageField={viewModel.stageStatus === StageStatus.PLANNED || viewModel.stageStatus === StageStatus.VIP}
                    basicSignupPulText={viewModel.basicSignupPulText}
                    labelPrefix="modal-"
                />,
                {fullscreen: false, position: ModalPosition.RIGHT, closeCallback: () => setInterestModalVisibility(false), ariaLabel: getFormattedMessage('jm.components.InterestForm.header')}
            );
        }
    }, [user]);

    const setupContent = async () => {
        CookieStore.setSourceMedium();

        const apiFilters = convertFiltersForApi(initialFilterState);

        const response = await SearchService.fetchStages({
            filter: {
                ...apiFilters,
                stageStatus: [
                    StageStatus.PLANNED,
                    StageStatus.VIP,
                    StageStatus.FORSALE,
                    StageStatus.MOVE_IN_READY,
                    StageStatus.PRESELLING,
                ],
                geoSearch: viewModel.latitude && viewModel.longitude ? {
                    lat: viewModel.latitude,
                    lng: viewModel.longitude
                } : undefined,
                excludeStageIds: [viewModel.stageId],
                siteId: getSiteIdForQuery(),
            },
            sort: ['sortOrder:asc'],
            limit: 10,
        });

        const mapMarkers = await getMapStages();
        const connectedStages = await getConnectedStages();
        const url = new URL(window.location.href);
        const paths = url.pathname.split('/').filter((el) => el !== '');
        let countyUrl = SITE_URLS.searchUrl;

        if (getCurrentSite() !== SITE.FI) {
            countyUrl += paths[0] + '/' + paths[1] + '/';
        } else {
            countyUrl += paths[1] + '/' + paths[2] + '/';
        }

        setRelatedStages(response.data?.hits ?? []);
        setRelatedStagesSearchLink(countyUrl);
        setMapMarkers(mapMarkers);
        setConnectedStages(connectedStages);

        const hash = window.location.hash.replace('#', '');

        if (typeof scrollRefs[hash] === 'undefined') return;
        window.history.replaceState({}, document.title, window.location.origin + window.location.pathname);

        setTimeout(() => {
            scrollToReference(scrollRefs[hash]);
        }, 500);
    };

    const getConnectedStages = async () => {
        if (!viewModel.projectId) return [];

        const apiFilters = convertFiltersForApi(initialFilterState);
        const response = await SearchService.fetchStages({
            filter: {
                ...apiFilters,
                project: viewModel.projectId,
                excludeStageIds: [viewModel.stageId],
            },
            sort: ['sortOrder:asc']
        });

        return response.data?.hits ?? [];
    };

    const getResidenceSearchResult = () => {
        const result = {} as SearchResult;

        result.type = "stage";
        result.id = viewModel.stageId;
        result.stage = viewModel.stageId;

        return result;
    };

    const getMapStages = async () => {
        const areaTag = viewModel.tags.find(x => x.type === 'area');

        if (!areaTag) return [];

        const apiFilters = convertFiltersForApi(initialFilterState);
        const response = await SearchService.fetchStages({
            filter: {
                ...apiFilters,
                geoSearch: viewModel.latitude && viewModel.longitude ? {
                    lat: viewModel.latitude,
                    lng: viewModel.longitude,
                    radius: 1000,
                } : undefined,
            },
            sort: ['sortOrder:asc']
        });

        if (response.data?.hits) {
            return response.data.hits.map(x => ({residence: x, position: {lat: x._geo?.lat, lng: x._geo?.lng}}));
        } else {
            return [];
        }
    };

    React.useEffect(() => {
        if (window.location.hash.indexOf('objects') > -1) {
            scrollToReference(scrollRefs.objects, true);
        }
        if (window.location.hash.indexOf('about') > -1) {
            scrollToReference(scrollRefs.about, true);
        }
        if (window.location.hash.indexOf('facts') > -1) {
            scrollToReference(scrollRefs.facts, true);
        }
        if (window.location.hash.indexOf('images') > -1) {
            scrollToReference(scrollRefs.images, true);
        }
        if (window.location.hash.indexOf('map') > -1) {
            scrollToReference(scrollRefs.map, true);
        }
        if (window.location.hash.indexOf('overview') > -1) {
            scrollToReference(scrollRefs.overview, true);
        }
        if (window.location.hash.indexOf('showings') > -1) {
            scrollToReference(scrollRefs.showings, true);
            // Re-scroll multiple times to handle lazy-loaded content (instant to avoid jarring animations)
            const times = [500, 1000, 1500, 2000, 2500, 3000, 4000, 5000];
            times.forEach(delay => {
                setTimeout(() => scrollToReference(scrollRefs.showings, false), delay);
            });
        }
        if (window.location.hash.indexOf('interest') > -1 && window.location.hash.indexOf('interestmodal') === -1) {
            scrollToReference(scrollRefs.interest, true);
        }

        window.history.replaceState({}, document.title, window.location.origin + window.location.pathname + window.location.search);
    }, [window.location.hash]);

    const scrollToReference = (reference: React.RefObject<HTMLElement | null>, smooth: boolean = true) => {
        if (reference.current === null) return;

        // Define the focusable elements selectors
        const header = 'h1, h2';
        const focusableSelectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

        // Find the first focusable element within the div
        const firstFocusable = reference.current.querySelector<HTMLElement>(header);
        const secondFocusable = reference.current.querySelector<HTMLElement>(focusableSelectors);

        // Set focus if a focusable element is found
        if (firstFocusable) {
            // Headings aren't focusable by default, so .focus() is a no-op and keyboard
            // focus stays in the menu. Make the target heading programmatically focusable
            // and move focus to it; preventScroll leaves positioning to our smooth scroll
            // below instead of the browser's instant focus scroll.
            firstFocusable.setAttribute('tabindex', '-1');
            firstFocusable.focus({preventScroll: true});
        } else if (secondFocusable) {
            secondFocusable.focus({preventScroll: true});
        }

        window.scroll({
            top: (reference.current.getBoundingClientRect().top + window.scrollY) - (isMobile ? 78 : 110), //reference.current.offsetTop - 100,
            behavior: smooth ? 'smooth' : 'auto',
        });
    };
    const onMenuClick = (target: string) => (e: React.SyntheticEvent) => {
        e.preventDefault();
        scrollToReference(scrollRefs[target]);
    };

    const getButton = () => {
        if(viewModel.stageStatus === StageStatus.SOLD_OUT) return null;

        return <InterestButton
            isVipperiod={vipPeriod}
            isStage
            stageName={viewModel.headerDisplay}
            agent={viewModel.realEstateAgents && viewModel.realEstateAgents[0]}
            stageId={viewModel.stageId}
            stageStatus={viewModel.stageStatus}
            interestDone={interestDone}
            setInterestDone={setInterestDone}
            hideMessageField={viewModel.stageStatus === StageStatus.PLANNED || viewModel.stageStatus === StageStatus.VIP}
            basicSignupPulText={viewModel.basicSignupPulText}
        />;
    };

    const showInterestForm = () => {
        const vipPeriod = viewModel.stageStatus === StageStatus.VIP && isFuture(new Date(viewModel.vipEnd));

        if (user?.isLegalEntity) return false;
        if (vipPeriod && user?.isVip) return false;
        if (viewModel.stageStatus === StageStatus.SOLD_OUT) return false;
        if (viewModel.jmoyGradinaProject) return false;

        return true;
    };

    const bellowFoldCallback = (isVisible: boolean) => {
        if (bellowFold || !isVisible) return;
        setBellowFold(true);
    };

    useEffect(() => {
        CookieStore.setSourceMedium();
    }, [location.search]);

    const pageClass = classnames('project-page', {
        'original': viewModel.concept === ConceptType.JM_ORIGINAL,
        'premium': viewModel.concept === ConceptType.PREMIUM,
        'smarta': viewModel.concept === ConceptType.SMARTA_KVADRAT
    });
    const informationBoxActive = (
        viewModel.informationBoxText != null ||
        viewModel.informationBoxHeader != null) && (isPast(new Date(viewModel.informationBoxStartTime)) ||
        viewModel.informationBoxStartTime == null) && (isFuture(new Date(viewModel.informationBoxEndTime)) ||
        viewModel.informationBoxEndTime == null
    );

    const [isSticky, setIsSticky] = React.useState(false);

    const onStickyChange = ({status}) => {
        setIsSticky(status === Sticky.STATUS_FIXED);
    };

    return (
        <div className={pageClass}>

            {viewModel.structuredData && viewModel.structuredData.map((structuredData, index) => (
                <StructuredData key={`structured-data-${index}`} structuredData={structuredData}/>
            ))}

            {(viewModel.coOwningPossible && viewModel.coOwningStageHeadline && viewModel.coOwningPageUrl) &&
                <CoOwningBanner
                    headline={viewModel.coOwningStageHeadline}
                    text={viewModel.coOwningStageText}
                    url={viewModel.coOwningPageUrl}
                />
            }

            {vipPeriod &&
                <VipPeriodBanner
                    startDate={viewModel.vipStart}
                    onScrollClick={onMenuClick('objects')}
                    endDate={viewModel.vipEnd}
                    small
                    isStagePage
                />
            }

            <ImageHeader
                title={viewModel.headerDisplay}
                images={viewModel.images}
                heroImage={viewModel.heroImage}
                heroVideo={viewModel.heroVideo}
                heroVideoImage={viewModel.heroVideoImage}
                stageStatus={viewModel.stageStatus}
                concept={viewModel.concept}
                isMobile={isMobile}
                user={user}
                residence={getResidenceSearchResult()}
                galleryType={GalleryType.STAGE}
                showFavorite
                digitalShowingText={viewModel.digitalShowingText}
                digitalShowingLinkUrl={viewModel.digitalShowingLinkUrl}
                playVideoOnClick
            />

            <div className="project-page__content">
                <div className="project-nav">
                    <Sticky onStateChange={onStickyChange} innerZ={1010}>
                        <div
                            className={
                                isSticky ?
                                    'sticky-wrapper sticky-wrapper--sticky' :
                                    'sticky-wrapper'
                            }
                        >
                            <ImageHeaderMenu button={getButton()}>
                                <li>
                                    <A
                                        href="#"
                                        onClick={onMenuClick('overview')}
                                        variant={viewModel.concept === ConceptType.JM_ORIGINAL ? LinkStyle.DARK_BG : LinkStyle.DEFAULT}
                                        attachedEvent={gtmEventsStagePageStickyClick("overview")}
                                        title={getFormattedMessage('jm.components.StagePage.menuFact')}
                                    >
                                        <FormattedMessage id='jm.components.StagePage.menuFact'/>
                                    </A>
                                </li>
                                {residencesVisible && objects && objects.length > 0 &&
                                    <li>
                                        <A
                                            href="#"
                                            variant={viewModel.concept === ConceptType.JM_ORIGINAL ? LinkStyle.DARK_BG : LinkStyle.DEFAULT}
                                            onClick={onMenuClick('objects')}
                                            attachedEvent={gtmEventsStagePageStickyClick("pricelist")}
                                            title={getFormattedMessage(viewModel.constructionForm === ResidenceType.PLOT
                                                ? 'jm.components.StagePage.menuPricelistPlot'
                                                : 'jm.components.StagePage.menuPricelist')}
                                        >
                                            {viewModel.constructionForm !== ResidenceType.PLOT &&
                                                <FormattedMessage id='jm.components.StagePage.menuPricelist'/>
                                            }
                                            {viewModel.constructionForm === ResidenceType.PLOT &&
                                                <FormattedMessage
                                                    id='jm.components.StagePage.menuPricelistPlot'/>
                                            }
                                        </A>
                                    </li>
                                }

                                {hasShowing(viewModel.showings, viewModel.stageStatus, viewModel.showingText) &&
                                    <li>
                                        <A
                                            href="#"
                                            variant={viewModel.concept === ConceptType.JM_ORIGINAL ? LinkStyle.DARK_BG : LinkStyle.DEFAULT}
                                            onClick={onMenuClick('showings')}
                                            attachedEvent={gtmEventsStagePageStickyClick("showings")}
                                            title={getFormattedMessage('jm.components.StagePage.menuShowings')}
                                        >
                                            <FormattedMessage id='jm.components.StagePage.menuShowings'/>
                                        </A>
                                    </li>
                                }
                                {viewModel.images && viewModel.images.length > 0 &&
                                    <li>
                                        <A
                                            href="#"
                                            variant={viewModel.concept === ConceptType.JM_ORIGINAL ? LinkStyle.DARK_BG : LinkStyle.DEFAULT}
                                            onClick={onMenuClick('images')}
                                            attachedEvent={gtmEventsStagePageStickyClick("images")}
                                            title={getFormattedMessage('jm.components.StagePage.menuImages')}
                                        >
                                            <FormattedMessage id='jm.components.StagePage.menuImages'/>
                                        </A>
                                    </li>
                                }

                                {viewModel.inspirationArea && viewModel.inspirationArea.length > 0 &&
                                    <li>
                                        <A
                                            href="#"
                                            variant={viewModel.concept === ConceptType.JM_ORIGINAL ? LinkStyle.DARK_BG : LinkStyle.DEFAULT}
                                            onClick={onMenuClick('about')}
                                            attachedEvent={gtmEventsStagePageStickyClick("about")}
                                            title={getFormattedMessage('jm.components.StagePage.menuAbout')}
                                        >
                                            <FormattedMessage id='jm.components.StagePage.menuAbout'/>
                                        </A>
                                    </li>
                                }
                                {(viewModel.latitude && viewModel.latitude !== 0 && viewModel.longitude && viewModel.longitude !== 0) &&
                                    <li>
                                        <A
                                            href="#"
                                            variant={viewModel.concept === ConceptType.JM_ORIGINAL ? LinkStyle.DARK_BG : LinkStyle.DEFAULT}
                                            onClick={onMenuClick('map')}
                                            attachedEvent={gtmEventsStagePageStickyClick("map")}
                                            title={getFormattedMessage('jm.components.StagePage.menuMap')}
                                        >
                                            <FormattedMessage id='jm.components.StagePage.menuMap'/>
                                        </A>
                                    </li>
                                }
                            </ImageHeaderMenu>
                        </div>
                    </Sticky>
                </div>

                <div className="mobile-cta">
                    {viewModel.stageStatus !== StageStatus.SOLD_OUT &&
                        <UserAgent mobile tablet>
                            {showInterestButton && !viewModel.jmoyGradinaProject && getButton()}
                        </UserAgent>
                    }
                    {showRedirectMessage &&
                        <SiteSpecificContent sites={[SITE.BORATT, SITE.SENIORGARDEN]}>
                            <RedirectMessage/>
                        </SiteSpecificContent>

                    }
                </div>

                {viewModel.stageStatus === StageStatus.VIP && isPast(new Date(viewModel.vipEnd)) && !informationBoxActive &&
                    <InfoMessage headline={getFormattedMessage('jm.components.StagePage.vipPurchaseHeadline')}>
                        <P>
                            <FormattedMessage
                                id="jm.components.StagePage.vipPurchaseInfoText"
                                values={{
                                    stageName: viewModel.headerDisplay,
                                    purchaseStart: getVIPPurchaseStart(new Date(viewModel.vipStart)),
                                    purchaseEnd: getVIPPurchaseEnd(new Date(viewModel.vipEnd)),
                                }}
                            />
                        </P>
                    </InfoMessage>
                }
                {informationBoxActive &&
                    <InfoMessage headline={viewModel.informationBoxHeader}>
                        <P dangerouslySetInnerHTML={{__html: sanitizeHtml(viewModel.informationBoxText)}}/>
                    </InfoMessage>
                }

                <MainInfo
                    header={cleanFinnishNames(viewModel.headerDisplay)}
                    breadcrumbs={breadcrumbs}
                    viewModel={viewModel}
                    quickFactsType={QuickFactsType.STAGE}
                    scrollRefs={scrollRefs}
                    scrollTo={() => scrollToReference(scrollRefs.facts)}
                />

                {viewModel.hasExternalLinker && (residencesAvailable || isInEditMode) &&
                    <ExternalExplorerBlock stageId={viewModel.stageId} />
                }

                { residencesVisible &&
                    <div ref={scrollRefs.objects}>
                        <MainGrid noGap fullwidth>
                            <ResidenceExplorerV2 settings={{
                                constructionForm: viewModel.constructionForm as ResidenceType,
                                siteId: viewModel.siteId,
                                stageId: viewModel.stageId,
                                stageIds: [...viewModel.residenceExplorerMultiple, ...[viewModel.stageId]],
                                stageName: viewModel.headerDisplay,
                                stageStatus: viewModel.stageStatus,
                                pageType: ResidenceExplorerPageType.STAGE,
                                vipStart: viewModel.vipStart,
                                vipEnd: viewModel.vipEnd,
                                hideSoldObjectsInExplorer: viewModel.hideSoldObjectsInExplorer,
                                hideSoldObjects: viewModel.hideSoldObjects,
                                sunStudyExternalUrl: viewModel.sunstudyExternalUrl,
                                hasExplorer: viewModel.hasExplorer
                            }}/>

                            <PriceListV2
                                disposalForm={viewModel.disposalForm as DisposalForm}
                                constructionForm={viewModel.constructionForm as ResidenceType}
                                isCoOwning={viewModel.coOwningPossible}
                                isMultiProject={viewModel.residenceExplorerMultiple && viewModel.residenceExplorerMultiple.length > 0}
                            />
                            {parkingPlaces && 
                                <Parkings 
                                    objects={parkingPlaces} 
                                    parkingProject={false} 
                                />
                            }
                        </MainGrid>
                    </div>
                }

                {viewModel.nearbyStageWithParkingUrl && 
                    <ParkingLink url={viewModel.nearbyStageWithParkingUrl} />
                }

                {viewModel.concept !== ConceptType.JM_ORIGINAL && viewModel.uspModel &&
                    <Concept
                        concept={viewModel.concept}
                        uspModel={viewModel.uspModel}
                    />
                }

                <MainGrid fullwidth>
                    {viewModel.campaignBlock &&
                        <div className="project-teasers">
                            <ContentArea id="project-teasers-block-area" blocks={[viewModel.campaignBlock]}/>
                        </div>
                    }

                    {!viewModel.jmoyGradinaProject &&
                        <div className="project-information__wrapper" ref={scrollRefs.showings}>
                            <ResidenceAside
                                showings={viewModel.showings}
                                stageName={viewModel.headerDisplay}
                                stageId={viewModel.stageId}
                                stageStatus={viewModel.stageStatus}
                                isVip={user?.isVip}
                                isLoggedIn={isLoggedIn}
                                smarta={viewModel.concept === ConceptType.SMARTA_KVADRAT}
                                showingText={viewModel.showingText}
                                showingLinkText={viewModel.showingLinkText}
                                showingLinkUrl={viewModel.showingLinkUrl}
                                contacts={viewModel.realEstateAgents}
                                links={viewModel.links}
                            />
                        </div>
                    }
                </MainGrid>

                {(viewModel.coOwningPossible && viewModel.coOwningStageHeadline && viewModel.coOwningPageUrl) &&
                    <CoOwningBanner
                        big
                        headline={viewModel.coOwningStageHeadline}
                        text={viewModel.coOwningStageText}
                        url={viewModel.coOwningPageUrl}
                    />
                }

                {vipPeriod &&
                    <VipPeriodBanner
                        startDate={viewModel.vipStart}
                        endDate={viewModel.vipEnd}
                        isStagePage
                        onScrollClick={onMenuClick('objects')}
                    />
                }

                {viewModel.images !== null &&
                    <div ref={scrollRefs.images}>
                        <ImageGallery
                            galleryType={GalleryType.STAGE}
                            images={viewModel.images}
                            digitalShowingText={viewModel.digitalShowingText}
                            digitalShowingLinkUrl={viewModel.digitalShowingLinkUrl}
                            putVideoLast
                        />
                    </div>
                }

                <InView triggerOnce onChange={bellowFoldCallback}>
                    {({ref}) => (
                        <div ref={ref} className="visibility-sensor"></div>
                    )}
                </InView>

                {(viewModel.swanLabel || viewModel.safeBrf) &&
                    <LabelSection
                        swanLabel={viewModel.swanLabel}
                        swanPageUrl={viewModel.swanPageUrl}
                        safeBrf={viewModel.safeBrf}
                    />
                }

                {viewModel.searchResultArea.length > 0 &&
                    <ContentArea id="search-result-block-area" blocks={viewModel.searchResultArea}/>
                }

                {viewModel.searchResultArea.length < 1 && connectedStages.length > 0 && !viewModel.jmoyGradinaProject &&
                    <AccessibleSlider
                        id="connected-stages"
                        className="project-page__connected-stages"
                        header={getFormattedMessage("jm.components.StagePage.connectedStages")}
                    >
                        {connectedStages.map((stage) => {
                            return (
                                <ResidenceCard
                                    residence={stage} 
                                    key={`card-${stage.id}`}  
                                    cardType={ResidenceCardType.PROJECT} 
                                    horizontal={connectedStages.length === 1 && !isMobile} 
                                />
                            );
                        })}
                    </AccessibleSlider>
                }

                <div className="dynamic-content" ref={scrollRefs.about}>
                    {/* Content area for teasers, sub pages and video blocks */}
                    <ContentArea id="inspiration-area" blocks={viewModel.inspirationArea}/>
                </div>

                <ProjectTimeline timeline={viewModel.timeline}/>

                <div ref={scrollRefs.map} className="project-page__map-container">
                    {mapMarkers && mapMarkers.length > 0 &&
                        <MapWrapper
                            thirdPartyCookie={userCookieSettings.thirdParty}
                            residences={mapMarkers.map((marker) => marker.residence)}
                        />
                    }
                </div>

                {showInterestForm() &&
                    <Container>
                        <div ref={scrollRefs.interest}/>
                        <InterestForm
                            agent={viewModel.realEstateAgents && viewModel.realEstateAgents[0]}
                            stageName={viewModel.headerDisplay}
                            stageId={viewModel.stageId}
                            stageStatus={viewModel.stageStatus}
                            hideMessageField={viewModel.stageStatus === StageStatus.PLANNED || viewModel.stageStatus === StageStatus.VIP}
                            labelPrefix="on-site"
                            className="interest-form--on-site"
                            interestDone={interestDone}
                            setInterestDone={setInterestDone}
                        />
                    </Container>
                }

                {relatedStages && relatedStages.length > 0 &&
                    <AccessibleSlider
                        header={getFormattedMessage('jm.components.StagePage.suggestionSliderHeadline')}
                        link={getCurrentSite() === SITE.FI
                            ? SITE_URLS.searchUrl
                            : relatedStagesSearchLink}
                        linkText={getFormattedMessage("jm.components.StagePage.seeAll")}
                    >
                        {relatedStages.map((stage) => {
                            return (
                                <ResidenceCard
                                    residence={stage} 
                                    key={`suggested-stage-${stage.id}`}  
                                    cardType={ResidenceCardType.PROJECT} 
                                    horizontal={relatedStages.length === 1 && !isMobile} 
                                />
                            );
                        })}
                    </AccessibleSlider>
                }

                <ContentArea
                    id="corporate-links"
                    className="corporate-links-area"
                    hasGrid
                    blocks={viewModel.siteSpecificContentArea}
                />
            </div>
        </div>
    );
};
